import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { CELL_INPUT } from "../cells/styles";
import {
  focusEnterTarget,
  focusNextTabCell,
  focusPrevTabCell,
} from "../../utils/tabNavigation";
import { useCellOverlayRect } from "./cellOverlay";
import { useHoverTooltip } from "./useHoverTooltip";

/** The expanded editor is at least this wide, whatever the column's width. */
const MIN_PANEL_WIDTH = 380;
/**
 * Panel border plus slack, subtracted from the space the viewport allows so the
 * textarea starts scrolling just before the card would reach the screen edge.
 * (The textarea's own padding is already inside its `scrollHeight`.)
 */
const PANEL_CHROME_PX = 18;
/** Panel floor — one comfortable line, so a short value never looks broken. */
const PANEL_MIN_TEXT_HEIGHT = 28;

export interface ExpandableTextCellProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Test id of the inline input; the editor gets `<testId>-expanded`. */
  testId?: string;
  /** Accessible name for the expanded editor. */
  label?: string;
}

/**
 * Free-text cell for the recap flows table: an ordinary one-line input for the
 * ordinary case, which grows into a full-size editor the moment the value stops
 * fitting.
 *
 * Three affordances, in increasing order of commitment:
 *  1. a `…` marker in the cell whenever the value is clipped, so a truncated
 *     value is never mistaken for the whole value;
 *  2. **hover** → the shared `useHoverTooltip` panel prints it wrapped and
 *     complete (works on locked orders too, where there is no other way to read
 *     it);
 *  3. **focus, or typing past the edge** → an editor sitting exactly over the
 *     cell, as wide and as tall as the value needs.
 *
 * The inline `<input>` is never replaced — it stays the cell's `data-tab-cell`
 * anchor, so grid navigation, the guidance overlay and E2E locators keep
 * working, and short values behave exactly as they did before. The editor is
 * portaled to `document.body`: the flows table is an `overflow-x-auto`
 * container, which would otherwise clip anything the cell grew into.
 *
 * Keyboard contract matches every other editable cell — Enter commits and steps
 * down a row, Shift+Enter jumps to the add-product row, Tab moves along, Escape
 * collapses back to the cell. Writes are live (each keystroke reaches
 * `onChange`), so collapsing is never a save and Escape is never a revert.
 */
export function ExpandableTextCell({
  value,
  onChange,
  disabled,
  placeholder,
  testId,
  label,
}: Readonly<ExpandableTextCellProps>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /**
   * Escape means "give me the cell back", and the cell is only useful if it
   * stays. Without this latch, collapsing re-focuses the input, the focus
   * handler measures the same clipped value and re-opens the editor — Escape
   * would be a no-op, and so would every keystroke after it. Held until focus
   * genuinely leaves the cell, so the user can finish typing in place.
   */
  const suppressAutoExpandRef = useRef(false);

  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);

  const rect = useCellOverlayRect(expanded, inputRef, MIN_PANEL_WIDTH);

  // Hover reveal only earns its panel when something is actually hidden, and
  // never on top of the editor — which already shows the whole value.
  const { triggerProps, tooltip } = useHoverTooltip(
    clipped && !expanded ? (
      <span className="whitespace-pre-wrap break-words">{value}</span>
    ) : null,
  );

  /**
   * Reads the live DOM — the only reliable answer, since clipping depends on
   * the rendered font and on the column's current (drag-resizable) width.
   */
  const measureClipped = useCallback(() => {
    const el = inputRef.current;
    if (!el) return false;
    const isClipped = el.scrollWidth > el.clientWidth + 1;
    setClipped(isClipped);
    return isClipped;
  }, []);

  useLayoutEffect(() => {
    measureClipped();
  }, [value, measureClipped]);

  // A column drag changes the answer without changing the value.
  useEffect(() => {
    const el = inputRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measureClipped());
    observer.observe(el);
    return () => observer.disconnect();
  }, [measureClipped]);

  // Caret lands at the end rather than at 0: the editor opens either from a
  // focus on a value the user is about to append to, or mid-typing, and both
  // want the insertion point where the text stops.
  useLayoutEffect(() => {
    if (!expanded) return;
    const el = textareaRef.current;
    if (!el || document.activeElement === el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [expanded, rect]);

  useEffect(() => {
    if (!expanded) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (inputRef.current?.contains(target)) return;
      setExpanded(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [expanded]);

  const handleInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Returning from Escape: leave the caret where the user put it rather
      // than selecting the value they just chose to keep editing in place.
      if (suppressAutoExpandRef.current) return;
      if (measureClipped()) {
        setExpanded(true);
        return;
      }
      // Unclipped value: unchanged behaviour — select all, edit in place.
      e.target.select();
    },
    [measureClipped],
  );

  const handleInputBlur = useCallback(() => {
    suppressAutoExpandRef.current = false;
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      if (suppressAutoExpandRef.current) return;
      // Measured after the browser has laid the new value out, so the cell
      // hands over to the editor on the exact keystroke the text stops fitting
      // instead of one keystroke later.
      requestAnimationFrame(() => {
        if (document.activeElement !== inputRef.current) return;
        if (measureClipped()) setExpanded(true);
      });
    },
    [onChange, measureClipped],
  );

  /**
   * Navigation runs off the inline input, never off the textarea: the editor is
   * portaled out of the table, so `closest("[data-recap-table]")` from inside it
   * would find nothing and every Tab would dead-end.
   *
   * Focus moves BEFORE the panel unmounts — the other order strands focus on
   * `<body>` when the textarea disappears from under it.
   */
  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const anchor = inputRef.current;
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          suppressAutoExpandRef.current = true;
          setExpanded(false);
          anchor?.focus();
          break;
        case "Enter":
          e.preventDefault();
          if (anchor) focusEnterTarget(anchor, e.shiftKey);
          setExpanded(false);
          break;
        case "Tab":
          e.preventDefault();
          if (anchor) {
            if (e.shiftKey) focusPrevTabCell(anchor);
            else focusNextTabCell(anchor);
          }
          setExpanded(false);
          break;
      }
    },
    [],
  );

  const handlePanelBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      const next = e.relatedTarget as Node | null;
      if (next && panelRef.current?.contains(next)) return;
      setExpanded(false);
    },
    [],
  );

  const showMarker = clipped && !expanded;

  const panel =
    expanded && rect ? (
      <div
        ref={panelRef}
        data-recap-portal
        data-testid={testId ? `${testId}-panel` : undefined}
        style={{
          position: "fixed",
          top: `${rect.top}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          zIndex: 9999,
        }}
        // Neutral card rather than the cell's blue focus accent: the panel sits
        // ON the cell, so it IS the focused control — a second focus ring on top
        // of the one showing through would just be noise.
        className="overflow-hidden rounded border border-input bg-background shadow-lg"
      >
        <AutoGrowTextarea
          ref={textareaRef}
          data-testid={testId ? `${testId}-expanded` : undefined}
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handlePanelKeyDown}
          onBlur={handlePanelBlur}
          placeholder={placeholder}
          minHeight={PANEL_MIN_TEXT_HEIGHT}
          maxHeight={Math.max(
            PANEL_MIN_TEXT_HEIGHT,
            rect.maxHeight - PANEL_CHROME_PX,
          )}
          className="block w-full whitespace-pre-wrap break-words border-0 bg-transparent px-2 py-1.5 text-xs leading-5 outline-none placeholder:text-muted-foreground"
        />
      </div>
    ) : null;

  return (
    <div className="relative w-full" {...triggerProps}>
      <input
        ref={inputRef}
        type="text"
        data-testid={testId}
        data-tab-cell
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        className={cn(CELL_INPUT, showMarker && "pr-5")}
      />
      {showMarker && (
        <MoreHorizontal
          aria-hidden="true"
          className="pointer-events-none absolute right-1 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground"
        />
      )}
      {tooltip}
      {typeof window !== "undefined" &&
        panel &&
        createPortal(panel, document.body)}
    </div>
  );
}
